import React, { useMemo, useState, useEffect } from 'react';
import { Box, Snackbar, Alert, Button, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import { useTimeTracking } from '../contexts/TimeTrackingContext';
import { CalendarEvent } from '../services/api';

const UPCOMING_WINDOW_MINUTES = 5;
const AUTO_DISMISS_AFTER_START_MINUTES = 5;

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Find a project whose name matches the event title (for "Switch to On Call"). */
function findMatchingProject(
  eventTitle: string,
  projects: { id: number; name: string }[]
): { id: number; name: string } | null {
  const title = eventTitle.trim().toLowerCase();
  if (!title) return null;
  // Exact match
  const exact = projects.find(p => p.name.trim().toLowerCase() === title);
  if (exact) return exact;
  // Project name contained in event title (e.g. "Client X - Call" contains "Client X")
  const contained = projects.find(p => {
    const pn = p.name.trim().toLowerCase();
    return pn && title.includes(pn);
  });
  if (contained) return contained;
  // Event title contained in project name
  const reverse = projects.find(p => {
    const pn = p.name.trim().toLowerCase();
    return pn && pn.includes(title);
  });
  return reverse || null;
}

/** Find the "On Call" task. */
function findOnCallTask(tasks: { id: number; name: string; scope: string }[]): { id: number; name: string } | null {
  return tasks.find(t => t.name === 'On Call') || null;
}

export default function UpcomingCallNotification() {
  const {
    upcomingEvents,
    calendarConnected,
    dismissedEventIds,
    dismissEvent,
    startTimer,
    switchTask,
    activeTimer,
    projects,
    tasks,
  } = useTimeTracking();

  const [open, setOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<CalendarEvent | null>(null);

  // Events that start within the next 5 minutes (or just started), not all-day, not dismissed.
  // Auto-dismiss window: hide after event start + 5 minutes.
  const upcomingSoon = useMemo(() => {
    if (calendarConnected !== true || !upcomingEvents.length) return [];
    const now = Date.now();
    const windowEnd = now + UPCOMING_WINDOW_MINUTES * 60 * 1000;
    const dismissAfter = AUTO_DISMISS_AFTER_START_MINUTES * 60 * 1000;
    return upcomingEvents
      .filter(e => !e.allDay)
      .filter(e => !dismissedEventIds.has(e.id))
      .filter(e => {
        const start = new Date(e.startTime).getTime();
        // Show if event starts within next 5 min, or has started but we're still within start+5min
        const startsSoon = start <= windowEnd;
        const notYetDismissed = now < start + dismissAfter;
        return startsSoon && notYetDismissed;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [upcomingEvents, calendarConnected, dismissedEventIds]);

  // Pick the first event to show (closest to now)
  useEffect(() => {
    if (upcomingSoon.length === 0) {
      setCurrentEvent(null);
      setOpen(false);
      return;
    }
    const event = upcomingSoon[0];
    const now = Date.now();
    const start = new Date(event.startTime).getTime();
    // Auto-dismiss if event started more than 5 minutes ago
    if (now > start + AUTO_DISMISS_AFTER_START_MINUTES * 60 * 1000) {
      dismissEvent(event.id);
      setCurrentEvent(null);
      setOpen(false);
      return;
    }
    setCurrentEvent(event);
    setOpen(true);
  }, [upcomingSoon, dismissEvent]);

  const handleDismiss = () => {
    if (currentEvent) {
      dismissEvent(currentEvent.id);
      setOpen(false);
      setCurrentEvent(null);
    }
  };

  const handleSwitchToOnCall = async () => {
    if (!currentEvent) return;
    const project = findMatchingProject(currentEvent.title, projects);
    const onCallTask = findOnCallTask(tasks);
    if (!project || !onCallTask) {
      handleDismiss();
      return;
    }
    if (activeTimer) {
      await switchTask(project.id, onCallTask.id);
    } else {
      await startTimer(project.id, onCallTask.id);
    }
    handleDismiss();
  };

  const project = currentEvent ? findMatchingProject(currentEvent.title, projects) : null;
  const onCallTask = findOnCallTask(tasks);
  const canSwitch = Boolean(project && onCallTask);

  if (!currentEvent || !open) return null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{ mb: 2, mr: 2 }}
    >
      <Alert
        severity="info"
        icon={<PhoneIcon />}
        onClose={handleDismiss}
        action={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {canSwitch && (
              <Button
                size="small"
                color="primary"
                variant="contained"
                onClick={handleSwitchToOnCall}
                sx={{ textTransform: 'none', fontSize: '0.78rem' }}
              >
                Switch to On Call
              </Button>
            )}
            <Button
              size="small"
              color="inherit"
              onClick={handleDismiss}
              sx={{ minWidth: 0, px: 0.5 }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </Button>
          </Box>
        }
        sx={{
          width: '100%',
          '& .MuiAlert-message': { flex: 1 },
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '14px' }}>
          {currentEvent.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.78rem' }}>
          Starts at {formatTime(currentEvent.startTime)}
        </Typography>
      </Alert>
    </Snackbar>
  );
}
